import {
  Box,
  Input,
  chakra,
  useColorModeValue,
  forwardRef,
  Center,
  Flex,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  Button,
} from '@chakra-ui/react';
import { throttle } from 'es-toolkit';
import React from 'react';
import type { ChangeEvent, FormEvent, FocusEvent } from 'react';

import useIsMobile from 'lib/hooks/useIsMobile';
import Menu from 'ui/shared/chakra/Menu';
import ClearButton from 'ui/shared/ClearButton';
import IconSvg from 'ui/shared/IconSvg';

interface Props {
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onBlur?: (event: FocusEvent<HTMLFormElement>) => void;
  onFocus?: () => void;
  onHide?: () => void;
  onClear: () => void;
  isHomepage?: boolean;
  isSuggestOpen?: boolean;
  value: string;
}

const SearchBarInput = (
  { onChange, onSubmit, isHomepage, isSuggestOpen, onFocus, onBlur, onHide, onClear, value }: Props,
  ref: React.ForwardedRef<HTMLFormElement>,
) => {
  const innerRef = React.useRef<HTMLFormElement>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLFormElement, []);
  const isMobile = useIsMobile();

  const handleScroll = React.useCallback(() => {
    const TOP_BAR_HEIGHT = 36;
    const clientRect = isMobile && innerRef?.current?.getBoundingClientRect();
    if (clientRect && clientRect.y < TOP_BAR_HEIGHT) {
      onHide?.();
    }
  }, [ isMobile, onHide ]);

  const handleChange = React.useCallback((event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  }, [ onChange ]);

  React.useEffect(() => {
    if (!isMobile) {
      return;
    }
    const throttledHandleScroll = throttle(handleScroll, 300);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [ isMobile, handleScroll ]);

  const handleKeyPress = React.useCallback((event: KeyboardEvent) => {
    if (isMobile) {
      return;
    }

    switch (event.key) {
      case '/': {
        if ([ 'INPUT', 'TEXTAREA' ].includes((event.target as HTMLElement).tagName)) {
          break;
        }

        if (!isSuggestOpen) {
          event.preventDefault();
          innerRef.current?.querySelector('input')?.focus();
          onFocus?.();
        }
        break;
      }
      case 'Escape': {
        if (isSuggestOpen) {
          innerRef.current?.querySelector('input')?.blur();
          onHide?.();
        }
        break;
      }
    }
  }, [ isMobile, isSuggestOpen, onFocus, onHide ]);

  React.useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [ handleKeyPress ]);

  const bgColor = useColorModeValue('white', 'black');

  const FILTER_OPTIONS = [ 'All Filters', 'Address', 'Txn Hash', 'Block', 'Token', 'Domain Name' ] as const;
  const [ selectedFilter, setSelectedFilter ] = React.useState<string>(FILTER_OPTIONS[0]);

  const handleHomeSubmit = React.useCallback((e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(e);
  }, [ onSubmit ]);

  const handleFilterOptionClick = React.useCallback((e: React.MouseEvent<HTMLElement>) => {
    const option = (e.currentTarget as HTMLElement).getAttribute('data-filter-option');
    if (option) setSelectedFilter(option);
  }, []);

  // Homepage: white rounded bar — All Filters | Input | Search button; border on focus
  if (isHomepage) {
    return (
      <chakra.form
        ref={ innerRef }
        noValidate
        onSubmit={ handleHomeSubmit }
        onBlur={ onBlur }
        w="100%"
        maxW="800px"
        minW={ 0 }
      >
        <Flex
          bg="white"
          borderRadius="10px"
          border="1px solid"
          borderColor="gray.200"
          overflow="hidden"
          h="48px"
          alignItems="stretch"
          boxShadow="0 1px 2px rgba(0,0,0,0.05)"
          _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
          // _focusWithin={{
          //   borderColor: 'blue.400',
          //   boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.35)',
          //   outline: 'none',
          // }}
          transition="border-color 0.15s, box-shadow 0.15s"
        >
          { /* 1. All Filters */ }
          <Menu placement="bottom-start" gutter={ 0 } strategy="fixed">
            <MenuButton
              as={ Flex }
              type="button"
              alignItems="center"
              justifyContent="center"
              gap={ 2 }
              px={{ base: 2, sm: 3 }}
              color="gray.600"
              fontSize="15px"
              fontWeight={ 500 }
              whiteSpace="nowrap"
              bg="white"
              _hover={{ bg: 'gray.50' }}
              _dark={{
                color: 'gray.400',
                bg: 'gray.800',
                _hover: { bg: 'gray.700' },
              }}
              flexShrink={ 0 }
              display={{ base: 'none', sm: 'flex' }}
              h="100%"
              // minH="48px"
              borderRadius="10px 0 0 10px"
            >
              <Flex as="span" alignItems="center" gap={ 8 } display="inline-flex">
                <Box as="span" lineHeight="1">{ selectedFilter }</Box>
                <Box
                  as="span"
                  w={ 5 }
                  h={ 5 }
                  lineHeight={ 0 }
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={ 0 }
                >
                  <IconSvg name="arrows/east-mini" boxSize={ 5 } transform="rotate(-90deg)" color="inherit"/>
                </Box>
              </Flex>
            </MenuButton>
            <Portal>
              <MenuList minW="200px" py={ 1 } zIndex={ 9999 }>
                { FILTER_OPTIONS.map((option) => (
                  <MenuItem
                    key={ option }
                    fontSize="14px"
                    data-filter-option={ option }
                    onClick={ handleFilterOptionClick }
                  >
                    { option }
                  </MenuItem>
                )) }
              </MenuList>
            </Portal>
          </Menu>

          { /* 2. Search input */ }
          <Input
            flex={ 1 }
            border="none"
            h="100%"
            px={ 2 }
            py={ 0 }
            fontSize="14px"
            placeholder="Search by Address / Txn Hash / Block / Token / Domain Name"
            _placeholder={{ color: 'gray.600', fontSize: '15px', fontWeight: '500' }}
            _focus={{ boxShadow: 'none', outline: 'none' }}
            _focusVisible={{ boxShadow: 'none' }}
            onChange={ handleChange }
            onFocus={ onFocus }
            value={ value ?? '' }
            minW={ 0 }
            bg="white"
            _dark={{ bg: 'gray.800' }}
            autoComplete="off"
          />

          { value ? (
            <Center px={ 1.5 } bg="white" _dark={{ bg: 'gray.800' }}>
              <ClearButton onClick={ onClear }/>
            </Center>
          ) : null }

          { /* 3. Search button with icon */ }
          <Button
            type="submit"
            padding="0px"
            w="36px"
            h="35px"
            margin="5px"
            // minW="40px"
            // minH="40px"
            flexShrink={ 0 }
            bg="#3672E9"
            color="white"
            borderRadius="6px"
            _hover={{ bg: '#2860d4' }}
            _active={{ bg: '#2860d4' }}
          >
            <IconSvg name="search" boxSize={ 4 }/>
          </Button>
        </Flex>
      </chakra.form>
    );
  }

  // Etherscan-style compact header search bar for non-homepage
  return (
    <chakra.form
      ref={ innerRef }
      noValidate
      onSubmit={ onSubmit }
      onBlur={ onBlur }
      onFocus={ onFocus }
      w="100%"
      backgroundColor={ isMobile ? bgColor : 'transparent' }
      borderRadius={{ base: 'none', lg: 'base' }}
      position={{ base: 'relative', lg: 'relative' }}
      top={{ base: 0, lg: 0 }}
      left="0"
      zIndex={{ base: 'auto', lg: isSuggestOpen ? 'popover' : 'auto' }}
      paddingX={{ base: 3, lg: 0 }}
      paddingTop={{ base: 2, lg: 0 }}
      paddingBottom={{ base: 2, lg: 0 }}
      boxShadow="none"
      transitionProperty="background-color,color,border-color"
      transitionDuration="normal"
      transitionTimingFunction="ease"
    >
      { /* Search bar: theme-aware bg (dark in dark theme), no white on hover */ }
      <Flex
        bg="surface_muted"
        borderRadius="6px"
        border="1px solid"
        borderColor="card_border"
        overflow="hidden"
        h="38px"
        alignItems="center"
        boxShadow="none"
        _hover={{ bg: 'surface_muted' }}
        _focusWithin={{
          borderColor: 'blue.400',
          boxShadow: '0 0 0 0.15rem rgba(66, 153, 225, 0.4)',
        }}
      >
        { /* Search icon - left, medium gray */ }
        <Center pl="12px" pr="8px" color="text_muted" flexShrink={ 0 }>
          <IconSvg name="search" boxSize={ 6 }/>
        </Center>

        { /* Search input - same gray bg as icon areas (surface_muted), no shadow */ }
        <Input
          flex={ 1 }
          border="none"
          h="100%"
          px={ 0 }
          pr={ 2 }
          fontSize="15px"
          color="text_primary"
          placeholder="Search by Address / Txn Hash / Block / Token / Domain Name"
          _placeholder={{ color: 'text_muted' }}
          _focus={{ boxShadow: 'none', outline: 'none' }}
          _focusVisible={{ boxShadow: 'none', bg: 'surface_muted' }}
          onChange={ handleChange }
          value={ value }
          bg="surface_muted !important"
          _hover={{ bg: 'surface_muted' }}
        />

        { /* Clear button (when has value) or keyboard shortcut "/" at end */ }
        { value ? (
          <Center pr="10px" pl="4px">
            <ClearButton onClick={ onClear } boxSize={ 5 }/>
          </Center>
        ) : (
          <Center pr="10px" pl="4px" display={{ base: 'none', lg: 'flex' }}>
            <Center
              w="26px"
              h="25px"
              borderRadius="6px"
              border="1px solid"
              borderColor="card_border"
              bg="#C3C7CD"
              color="#fff"
              fontSize="14px"
              fontWeight="500"
            >
              /
            </Center>
          </Center>
        ) }
      </Flex>
    </chakra.form>
  );
};

export default React.memo(forwardRef(SearchBarInput));